import { Button, CircularProgress, Typography } from '@mui/material'
import clsx from 'clsx'
import RadioButton from 'components/RadioButton/RadioButton'
import TabPanel from 'components/TabPanel/TabPanel'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import { ReactComponent as CancelSvg } from 'images/icons/cancel.svg'
import { ReactComponent as SaveSvg } from 'images/icons/save.svg'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useMissionStore from 'store/mission'
import { MISSION_ZONE_TYPES, PROGRESS_TYPES, SUB_CLEAN_MODES } from 'utils/constants'
import { ICleaningModeConfig, ICleaningZone, IZone, IZoneDetailsData } from 'utils/interfaces'
import { useShallow } from 'zustand/react/shallow'

import AutoCleaningParams from './AutoCleaningParams'
import ManualCleaningParams from './ManualCleaningParams'
import ZonePointSettings from './ZonePointSettings/ZonePointSettings'

import style from './ZoneDetailsView.module.css'

const ZoneDetailsView = ({
  zone,
  onSave,
  isPending,
}: {
  zone?: IZone
  onSave: (data: IZoneDetailsData) => void
  isPending: boolean
}) => {
  const { tempZone, savingProgress, cleaningZones, cancelEditingZone, tempStartingPoint, tempEndingPoint } =
    useMissionStore(
      useShallow(state => ({
        tempZone: state.tempZone,
        savingProgress: state.savingProgress,
        cleaningZones: state.cleaningZones,
        cancelEditingZone: state.cancelEditingZone,
        tempStartingPoint: state.tempStartingPoint,
        tempEndingPoint: state.tempEndingPoint,
      }))
    )
  const { t } = useTranslation()
  const [selectedZoneType, setSelectedZoneType] = useState(zone?.properties?.zone_type || MISSION_ZONE_TYPES.CLEANING)
  const [name, setName] = useState(zone?.properties?.name || `New Zone ${cleaningZones.length + 1}`)
  const [description, setDescription] = useState(zone?.properties?.description || '')
  const initialCleaningConfig = zone && 'cleaningConfig' in zone.properties
    ? (zone.properties as ICleaningZone['properties']).cleaningConfig
    : undefined
  const [autoConfig, setAutoConfig] = useState<ICleaningModeConfig | undefined>(
    initialCleaningConfig?.mode === SUB_CLEAN_MODES.AUTO
      ? initialCleaningConfig
      : zone ? undefined : { mode: SUB_CLEAN_MODES.AUTO, autoMode: 1 }
  )
  const [manualConfig, setManualConfig] = useState<ICleaningModeConfig | undefined>(
    initialCleaningConfig?.mode === SUB_CLEAN_MODES.MANUAL ? initialCleaningConfig : undefined
  )
  const [tab, setTab] = useState(initialCleaningConfig?.mode === SUB_CLEAN_MODES.MANUAL ? 1 : 0)
  const cleaningConfig = tab === 0 ? autoConfig : manualConfig

  const changeTab = (nextTab: number) => {
    setTab(nextTab)
    if (nextTab === 0 && !autoConfig) {
      setAutoConfig({ mode: SUB_CLEAN_MODES.AUTO, autoMode: 1 })
    }
  }

  const changeZoneType = useCallback((zoneType: MISSION_ZONE_TYPES) => {
    setSelectedZoneType(zoneType)
  }, [])

  const onSaveClicked = () => {
    onSave({
      name,
      description,
      zoneType: selectedZoneType,
      cleaningConfig: selectedZoneType === MISSION_ZONE_TYPES.CLEANING ? cleaningConfig : undefined,
    })
  }

  const onCancelClicked = () => {
    cancelEditingZone()
  }

  const savingProgressInWork = useMemo(() => savingProgress === PROGRESS_TYPES.WORK, [savingProgress])

  const pointsIncomplete = !!tempEndingPoint && !tempStartingPoint

  const isSaveDisabled = (!zone && !tempZone) || savingProgressInWork || isPending || pointsIncomplete || (
    selectedZoneType === MISSION_ZONE_TYPES.CLEANING && tab === 1 && !manualConfig
  )

  return (
    <div className={style.modal}>
      <div className={style.head}>
        <Typography variant="h5" className={style.title}>
          {t('zone_details.zone_details')}
        </Typography>
      </div>
      <div className={style.content}>
        <div>
          {!zone && (
            <div>
              <div className="text-14-400">{t('zone_details.zone_type')}</div>
              <div className={style.radioButtons}>
                <div className={style.radioButton}>
                  <RadioButton
                    checked={selectedZoneType === MISSION_ZONE_TYPES.CLEANING}
                    label={t('zone_details.type_cleaning')}
                    name="zoneType"
                    onChange={() => changeZoneType(MISSION_ZONE_TYPES.CLEANING)}
                  />
                </div>
                <div className={style.radioButton}>
                  <RadioButton
                    checked={selectedZoneType === MISSION_ZONE_TYPES.RESTRICT}
                    label={t('zone_details.type_restrict')}
                    name="zoneType"
                    onChange={() => changeZoneType(MISSION_ZONE_TYPES.RESTRICT)}
                  />
                </div>
              </div>
            </div>
          )}
          <TextFieldDescribed
            className={style.textfield}
            description={t('zone_details.zone_name')}
            defaultValue={name}
            placeholder={t('zone_details.zone_name_placeholder')}
            onChange={e => setName(e.target.value)}
          />
          <TextFieldDescribed
            className={style.textfield}
            description={t('zone_details.zone_description')}
            defaultValue={description}
            placeholder={t('zone_details.zone_description_placeholder')}
            onChange={e => setDescription(e.target.value)}
          />
          {selectedZoneType === MISSION_ZONE_TYPES.CLEANING && (
            <div>
              <div className={clsx('text-14-400', style.subtitle)}>{t('zone_details.cleaning_mode')}</div>
              <div className={style.textfield}>
                <div className={style.radioButtons}>
                  <div className={style.radioButton}>
                    <RadioButton
                      checked={tab === 0}
                      label={t('zone_details.mode_auto')}
                      name="cleaningMode"
                      onChange={() => changeTab(0)}
                    />
                  </div>
                  <div className={style.radioButton}>
                    <RadioButton
                      checked={tab === 1}
                      label={t('zone_details.mode_manual')}
                      name="cleaningMode"
                      onChange={() => changeTab(1)}
                    />
                  </div>
                </div>
              </div>
              <div className={style.textfield}>
                <TabPanel value={tab} index={0} disablePadding>
                  <AutoCleaningParams config={autoConfig} onChange={setAutoConfig} />
                </TabPanel>
                <TabPanel value={tab} index={1} disablePadding>
                  <ManualCleaningParams config={manualConfig} onChange={setManualConfig} />
                </TabPanel>
              </div>
              <div className={clsx('text-14-400', style.subtitle, style.marginTop)}>
                {t('zone_details.start_point')}
              </div>
              <div className={style.textfield}>
                <ZonePointSettings kind="starting" />
              </div>
              <div className={clsx('text-14-400', style.subtitle, style.marginTop)}>{t('zone_details.end_point')}</div>
              <div className={style.textfield}>
                <ZonePointSettings kind="ending" />
              </div>
              {pointsIncomplete && (
                <div className={clsx('text-12-400', style.pointsIncomplete)}>{t('zone_details.points_incomplete')}</div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={style.footer}>
        <Button
          onClick={onCancelClicked}
          disabled={savingProgressInWork}
          color="error"
          variant="contained"
          fullWidth
          endIcon={<CancelSvg />}
        >
          {t('mission_settings.label_cancel')}
        </Button>
        <Button
          disabled={isSaveDisabled}
          onClick={onSaveClicked}
          fullWidth
          variant="contained"
          color="primary"
          endIcon={savingProgressInWork || isPending ? <CircularProgress size={16} color="secondary" /> : <SaveSvg />}
        >
          {t('mission_settings.label_save')}
        </Button>
      </div>
    </div>
  )
}

export default ZoneDetailsView
